import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";

/**
 * FIX-049 stage 8: the Approved Reels (SRRs) of the selected persona - one row per approved Reel, independent of any single run. Clicking a row opens everything stored about that SRR.
 * Implementation options of an SRR: RP (the existing video process, started by the user themselves); Get JSON and RE (FIX-011) are future options shown as disabled (coming soon / not connected yet).
 */
export interface SrrSummaryView {
  readonly id: string;
  readonly reelId: string;
  readonly canonicalUrl: string;
  readonly creator: string;
  readonly relativePerformance: number;
  readonly approvedAt: string;
  /** The Reel's MP4 was removed by Research Media Retention (the SRR itself is unchanged). */
  readonly mp4Removed: boolean;
}
export interface SrrDetailView extends SrrSummaryView {
  readonly personaId: string;
  readonly sourceRunId: string;
  readonly sourceRuleVersion: string;
  readonly snapshot: {
    readonly persona: { readonly persona_id: string; readonly subject_key: string; readonly display_name: string; readonly identity_version: string; readonly reference_set_key: string; readonly reference_asset_ids: readonly string[]; readonly voice_audio_asset_ref: string | null };
    readonly reel: { readonly caption: string | null; readonly uploaded_at: string | null; readonly collected_metrics: Record<string, number | boolean | string | null> };
    readonly performance: { readonly rule_version: string; readonly status: string; readonly interesting: boolean; readonly views: number | null; readonly creator_median_views: number | null; readonly relative_performance: number | null; readonly maturity: string; readonly like_rate: number | null; readonly comment_rate: number | null };
    readonly content_analysis: {
      readonly prompt_version: string;
      readonly schema_version: string;
      readonly status: string;
      readonly model: string | null;
      readonly facts: { readonly reel: { readonly songName: string | null } } | null;
      readonly ai_interpretation: { readonly summary: string; readonly structure: readonly { readonly atSeconds: number | null; readonly description: string }[]; readonly hook: string | null; readonly notableElements: readonly string[]; readonly likelyAttentionDrivers: readonly { readonly driver: string; readonly rationale: string }[]; readonly limitations: readonly string[] } | null;
    } | null;
    readonly media: { readonly mp4: { readonly gcs_uri: string; readonly size_bytes: number } | null; readonly thumbnail: { readonly gcs_uri: string } | null };
    readonly review: { readonly decision: string; readonly decided_by_user_id: string | null; readonly decided_at: string; readonly source_run_id: string };
  };
}

const ERROR_TEXT: Record<string, string> = {
  rp_handoff_not_configured: "The RP hand-off is not configured on this server, so nothing was prepared.",
  rp_media_missing: "The stored source video of this SRR is not available (removed by Research Media Retention, or never stored). Nothing was started and nothing was fetched again.",
  rp_handoff_failed: "The source video could not be registered. Nothing was started; try again later.",
};
const dash = (v: number | string | null | undefined): string => (v === null || v === undefined ? "—" : String(v));

export function ReelApproved({ base, personaId, csrfToken, canAct, refreshKey }: { readonly base: string; readonly personaId: string; readonly csrfToken: string | null; readonly canAct: boolean; readonly refreshKey: number }) {
  const [rows, setRows] = useState<readonly SrrSummaryView[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SrrDetailView | null>(null);
  const [handoff, setHandoff] = useState<{ sourceVideoId: string; personaDisplayName: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const root = `${base}/personas/${encodeURIComponent(personaId)}`;

  const loadList = useCallback(async () => {
    try {
      const r = await api.get<{ srrs: readonly SrrSummaryView[] }>(`${root}/srrs`);
      if (mounted.current) setRows(r.srrs);
    } catch {
      if (mounted.current) setError("Could not load the approved Reels.");
    }
  }, [root]);
  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setDetail(null);
    setHandoff(null);
    setError(null);
    void loadList();
  }, [loadList, refreshKey]);

  const open = async (srrId: string) => {
    if (openId === srrId) {
      setOpenId(null);
      return;
    }
    setOpenId(srrId);
    setDetail(null);
    setHandoff(null);
    setError(null);
    try {
      const r = await api.get<{ srr: SrrDetailView }>(`${root}/srrs/${encodeURIComponent(srrId)}`);
      if (mounted.current) setDetail(r.srr);
    } catch {
      if (mounted.current) setError("Could not load the SRR.");
    }
  };

  const errorText = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    return (body?.error !== undefined ? ERROR_TEXT[body.error] : undefined) ?? body?.message ?? fallback;
  };

  const prepareRp = async (srrId: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sourceVideoId: string; personaDisplayName: string }>(`${root}/srrs/${encodeURIComponent(srrId)}/rp`, csrfToken);
      if (mounted.current) setHandoff(r);
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not prepare the source video."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const ai = detail?.snapshot.content_analysis?.ai_interpretation ?? null;
  return (
    <section data-testid="reel-approved">
      <h3>Approved Reels</h3>
      <p className="muted">The Reels approved for use for this persona (one row per Reel). Click a row for everything stored about it.</p>
      {rows !== null && rows.length === 0 ? <p className="muted">No approved Reels yet.</p> : null}
      {rows !== null && rows.length > 0 ? (
        <table data-testid="approved-table">
          <thead>
            <tr>
              <th>Creator</th>
              <th>Reel</th>
              <th>Relative performance</th>
              <th>Approved</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid="approved-row" data-srr={r.id} onClick={() => void open(r.id)} style={{ cursor: "pointer" }}>
                <td>@{r.creator}</td>
                <td>{r.canonicalUrl.replace("https://www.instagram.com/", "")}</td>
                <td>{r.relativePerformance.toFixed(2)}×</td>
                <td>{r.approvedAt.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {detail !== null && openId === detail.id ? (
        <div data-testid="srr-detail">
          <h4>
            SRR — @{detail.creator} ·{" "}
            <a href={detail.canonicalUrl} target="_blank" rel="noreferrer">
              {detail.canonicalUrl}
            </a>
          </h4>
          <section data-testid="srr-persona">
            <h5>Persona</h5>
            <ul>
              <li>
                {detail.snapshot.persona.display_name} ({detail.snapshot.persona.subject_key}) · identity {detail.snapshot.persona.identity_version} · reference set {detail.snapshot.persona.reference_set_key}
              </li>
              <li>Reference assets: {detail.snapshot.persona.reference_asset_ids.length}</li>
              <li>Voice / audio reference: {detail.snapshot.persona.voice_audio_asset_ref ?? "none (no voice asset exists for this persona)"}</li>
            </ul>
          </section>
          <section data-testid="srr-facts">
            <h5>Facts (from the collected data)</h5>
            <ul>
              <li>Caption: {dash(detail.snapshot.reel.caption)}</li>
              <li>Uploaded: {dash(detail.snapshot.reel.uploaded_at)}</li>
              <li>
                Views {dash(detail.snapshot.performance.views)} · Likes {dash(detail.snapshot.reel.collected_metrics.likes as number | null)} · Comments {dash(detail.snapshot.reel.collected_metrics.comments as number | null)}
              </li>
              <li>
                Creator median views {dash(detail.snapshot.performance.creator_median_views)} · {dash(detail.snapshot.performance.relative_performance === null ? null : `${detail.snapshot.performance.relative_performance.toFixed(2)}×`)} ({detail.snapshot.performance.maturity}, rule {detail.snapshot.performance.rule_version})
              </li>
              <li>
                Approved {detail.snapshot.review.decided_at.slice(0, 16).replace("T", " ")} in run {detail.snapshot.review.source_run_id.slice(0, 8)}
              </li>
              <li data-testid="srr-source-video">Source video: {detail.snapshot.media.mp4 === null ? "none stored" : detail.mp4Removed ? "removed by Research Media Retention (the SRR is unchanged)" : "stored"} · Thumbnail: {detail.snapshot.media.thumbnail === null ? "none stored" : "stored"}</li>
            </ul>
          </section>
          <section data-testid="srr-ai">
            <h5>AI interpretation{detail.snapshot.content_analysis?.model ? ` (generated by ${detail.snapshot.content_analysis.model}; not fact)` : " (not available)"}</h5>
            {ai === null ? (
              <p className="muted">{detail.snapshot.content_analysis === null ? "No content analysis was stored for this Reel." : `Content analysis status: ${detail.snapshot.content_analysis.status}`}</p>
            ) : (
              <div>
                <p>{ai.summary}</p>
                {ai.hook ? <p>Opening: {ai.hook}</p> : null}
                <ol>
                  {ai.structure.map((x, i) => (
                    <li key={i}>
                      {x.atSeconds !== null ? `${x.atSeconds}s — ` : ""}
                      {x.description}
                    </li>
                  ))}
                </ol>
                <p>Notable elements: {ai.notableElements.join("; ") || "—"}</p>
                <h6>Likely attention drivers (AI hypotheses)</h6>
                <ul>
                  {ai.likelyAttentionDrivers.map((d, i) => (
                    <li key={i}>
                      {d.driver}
                      {d.rationale ? ` — ${d.rationale}` : ""}
                    </li>
                  ))}
                </ul>
                <h6>Limitations</h6>
                <ul>
                  {ai.limitations.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section data-testid="srr-implementation">
            <h5>Implementation options</h5>
            <p>
              <strong>RP</strong> — the existing video process, started by you.{" "}
              {canAct ? (
                <button type="button" data-testid="srr-prepare-rp" disabled={busy || detail.snapshot.media.mp4 === null || detail.mp4Removed} onClick={() => void prepareRp(detail.id)}>
                  Prepare source video for RP
                </button>
              ) : null}
            </p>
            {handoff !== null ? (
              <p data-testid="srr-rp-ready">
                The source video is saved (id {handoff.sourceVideoId.slice(0, 8)}). Open <a href="/generate/create/video">Create → Video</a>, choose it from your saved source videos and the persona {handoff.personaDisplayName}. Nothing was started.
              </p>
            ) : null}
            <p>
              <strong>Get JSON</strong> —{" "}
              <button type="button" data-testid="srr-get-json" disabled title="Get JSON is not available yet">
                Coming soon
              </button>
            </p>
            <p>
              <strong>RE</strong> —{" "}
              <a data-testid="srr-re" href={`/generate/create/re?srrId=${encodeURIComponent(detail.id)}&personaId=${encodeURIComponent(personaId)}`}>
                Recreate with RE
              </a>
            </p>
          </section>
        </div>
      ) : null}
      {error !== null ? (
        <p role="alert" data-testid="approved-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
